// ── #567 · SCREEN 2 — the per-line answer: "What can San Antonio send?" ──────
//
// EXTRACTED at Session 79.22b.4.4 (#698 half a, gate 665 D25) from the Transfers
// sheet, where it was built at 79.15i, because the supplying branch's manager
// now answers each item from the Requests inbox card as well. Colby, reviewing
// gate 665: "managers need to be able to deny, approve individual lines of a
// multi item request." It worked on the Transfers sheet only.
//
// ONE COMPONENT, TWO SCREENS — locked rule 14. The same answer given from two
// places must be one implementation, or the two drift in exactly one of them.
// Every word here is gate 567's approved wording, moved rather than rewritten:
// the sheet reads exactly as it did before the move.
//
// It owns only the FORM. The press goes to the caller's `onSubmit(answers)`,
// which calls answerTransferLines() -- the one function both screens use, and
// behind it answer_branch_transfer_lines(), which checks who is calling (#616).
// Showing this form is advisory; the database decides.
//
// Colby's words on why the item level matters: "each item being transferred
// has to be able to be accounted for and tracked at the individual level... if
// one item's status is dependent on another item that system will always wind
// up failing." Austin has the MDO and no frames. Their only honest button was
// Deny, which refused the MDO they were standing next to.
import React, { useState } from 'react';
import ActionButton from '../ActionButton';
import { controlHeight } from '../../lib/tiers';
import { refusedItemsPhrase } from '../../lib/branchTransfers';
import { Actions, actionWidth } from './TransferActions';

// `extra(line)` draws under each item's name -- the inbox passes the giving
// shelf there, so the answer and the hold-back evidence sit on the same line
// (gate 665 D25, with D15).
//
// ── `stage="first"` — "ONLY SOME OF THESE ARE NEEDED" ──────────────────────
//
// #698 half (b), gate 665 D26, Session 79.22b.4.7.1. The asking branch's
// manager answers the same way on her own team's request: Needed or Not
// needed, a reason under each Not needed. SAME FORM, ITS OWN WORDS - F2's
// first-stage copy and the sentences Colby approved at 79.22b.4.7.1's open
// (docs/audits/79.22b.4.7.1/APPROVED_DRAWING_698_572_FIRST_STAGE.html). The
// answers leave as 'needed' / 'not_needed', endorse_branch_transfer_lines()'s
// own vocabulary. The second stage reads exactly as it did.
export default function PerItemAnswer({
  lines, items = {}, supplier = null, asker = null, busy = false,
  isMobile = false, coarse = false, onSubmit, onCancel, extra = null,
  stage = 'supply',
}) {
  const [lineAnswers, setLineAnswers] = useState({});
  const [lineReasons, setLineReasons] = useState({});
  const first = stage === 'first';
  const answerOf = l => lineAnswers[String(l.item_id)];
  const reasonOf = l => lineReasons[String(l.item_id)] || '';
  const answeredCount = lines.filter(l => answerOf(l)).length;
  const refusedLines = lines.filter(l => answerOf(l) === 'refused');
  const acceptedCount = lines.filter(l => answerOf(l) === 'accepted').length;
  const missingReason = refusedLines.find(l => !reasonOf(l).trim());
  const nameOf = l => items[String(l.item_id)]?.name;
  // F3: the button answers the press rather than sitting dead. Unanswered lines
  // first, because that is the one the approver is most likely to hit.
  const answerBlocked =
    answeredCount < lines.length
      ? `Answer every item first — ${lines.length - answeredCount} still to go.`
      : missingReason
        ? (first
            ? `Say why ${nameOf(missingReason) ? `the ${nameOf(missingReason)}` : 'that item'} isn't needed — it's the only message the person who asked gets.`
            : `Say why you can't send ${nameOf(missingReason) || 'that item'} — it's the only message ${asker || 'the other branch'} gets.`)
        : null;
  // #619's rule, as a sentence's subject: ONE refused item is named.
  const phrase = refusedItemsPhrase(refusedLines, items);
  const refusedOne = phrase !== 'the rest';

  return (
    <div>
      <div style={{ fontSize: 11.5, fontWeight: 700, color: '#5C5C5C', textTransform: 'uppercase',
                    letterSpacing: '0.8px', marginBottom: 7 }}>
        {first
          ? <>Which of these does {asker || 'the branch that asked'} need?</>
          : <>What can {supplier || 'this branch'} send?</>}
      </div>

      {/* CARDS, NOT A TABLE. F4: on a phone the per-line answer is one
          card per line with full-height buttons, because a two-column
          table at 375px puts a 40px target next to a wrapping name. */}
      {lines.map(l => {
        const a = answerOf(l);
        return (
          <div key={l.item_id} style={{ border: '1px solid #E4E4E4', borderRadius: 8,
                                        padding: '10px 11px', marginBottom: 7 }}>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: '#0F0F0F' }}>
              {l.qty_requested} × {items[String(l.item_id)]?.name || `Item ${l.item_id}`}
            </div>
            {extra && extra(l)}
            <div style={{ display: 'flex', gap: 7, marginTop: 8, flexWrap: 'wrap' }}>
              {(first ? [['accepted', 'Needed'], ['refused', 'Not needed']] : [['accepted', 'Can send'], ['refused', "Can't"]]).map(([val, label]) => (
                <button
                  key={val}
                  onClick={() => setLineAnswers(m => ({ ...m, [String(l.item_id)]: val }))}
                  className={a === val ? (val === 'accepted' ? 'btn btn-primary' : 'btn btn-danger') : 'btn'}
                  style={{
                    minHeight: controlHeight(coarse, 'min'), fontSize: 12,
                    ...(a === val ? {} : { background: '#F1F1F1', color: '#3A3A3A' }),
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
            {a === 'refused' && (
              <div style={{ marginTop: 8 }}>
                {/* THE REASON OPENS BENEATH THE LINE IT BELONGS TO — callout C.
                    One box under two refused lines produces one sentence
                    covering two different facts, which is the whole
                    header-level defect reappearing inside the fix. */}
                <label style={{ fontSize: 11.5, fontWeight: 700, color: '#8A2B22', display: 'block', marginBottom: 4 }}>
                  {first
                    ? "Why aren't these needed? The person who asked sees this."
                    : <>Why can't you send these? {asker || 'The other branch'} sees this.</>}
                </label>
                <textarea
                  value={reasonOf(l)}
                  onChange={e => setLineReasons(m => ({ ...m, [String(l.item_id)]: e.target.value }))}
                  rows={2}
                  placeholder={first ? 'We have some on the rack, the job changed, …' : 'None spare, back in stock Thursday, …'}
                  style={{ width: '100%', fontSize: 16, padding: 8, border: '1px solid #DDD',
                           borderRadius: 8, boxSizing: 'border-box' }}
                />
              </div>
            )}
          </div>
        );
      })}

      {/* The count makes the outcome unambiguous BEFORE the press rather
          than after — callout D.

          FOUND ON THE LIVE SCREEN, 79.15i: this summary originally keyed
          on `acceptedCount === 0`, which is ALSO true of a form nobody
          has touched — so it opened by announcing "San Antonio can't
          send any of this" before a single button had been pressed, and
          the commit button read "Refuse this request". A sentence that
          states a fact must be true, and that one asserted a conclusion
          nobody had reached. It says nothing until every line is
          answered. */}
      {answeredCount === lines.length && first ? (
        // F2's first-stage summary. Nothing is routed from here (#571): the
        // asking branch has decided these are not needed, so nobody is asked.
        <div style={{ fontSize: 12.5, color: '#3A3A3A', margin: '10px 0 9px' }}>
          {acceptedCount === 0
            ? <><strong>None of these go to {supplier || 'the other branch'}.</strong>{' '}
                The request is refused here, and {supplier || 'the other branch'} is never asked.</>
            : <><strong>{acceptedCount} of {lines.length} items {acceptedCount === 1 ? 'goes' : 'go'} to {supplier || 'the other branch'}.</strong>
                {refusedLines.length > 0 && <>{' '}{phrase.charAt(0).toUpperCase() + phrase.slice(1)} {refusedOne ? 'is' : 'are'} refused
                  here, and nobody is asked for {refusedOne ? 'it' : 'them'}.</>}</>}
        </div>
      ) : answeredCount === lines.length ? (
        <div style={{ fontSize: 12.5, color: '#3A3A3A', margin: '10px 0 9px' }}>
          {acceptedCount === 0
            ? <><strong>{supplier || 'This branch'} can't send any of this.</strong>{' '}
                {asker || 'They'} will be asked what to do instead.</>
            : <><strong>{supplier || 'This branch'} sends {acceptedCount} of {lines.length} items.</strong>{' '}
                {/* #619 -- gate 567 F2's rule, at every width: ONE refused
                    item is named, several are "the rest". */}
                {refusedLines.length > 0 && <>{asker || 'They'} will be asked what to do about {refusedItemsPhrase(refusedLines, items)}.</>}</>}
        </div>
      ) : (
        <div style={{ fontSize: 12.5, color: '#5C5C5C', margin: '10px 0 9px' }}>
          {first
            ? <>Say whether {asker || 'the branch that asked'} needs each item.</>
            : <>Say what {supplier || 'this branch'} can send for each item.</>}
        </div>
      )}

      <Actions isMobile={isMobile}>
        <ActionButton
          onClick={() => onSubmit(lines.map(l => (first
            ? {
                item_id: l.item_id,
                answer: answerOf(l) === 'refused' ? 'not_needed' : 'needed',
                reason: answerOf(l) === 'refused' ? reasonOf(l) : null,
              }
            : {
                item_id: l.item_id,
                answer: answerOf(l),
                reason: answerOf(l) === 'refused' ? reasonOf(l) : null,
              })))}
          blockedReason={answerBlocked}
          busy={busy}
          busyLabel="Sending…"
          // The RED, refusing variant only once every line has actually
          // been answered — see the note on the summary above. A button
          // that reads "Refuse this request" on an untouched form is
          // telling the approver what they are about to do, wrongly.
          className={answeredCount === lines.length && acceptedCount === 0 ? 'btn btn-danger' : 'btn btn-primary'}
          style={actionWidth(isMobile, coarse)}
        >
          {answeredCount === lines.length && acceptedCount === 0
            ? 'Refuse this request'
            : first
              ? `Send the needed items to ${supplier || 'the other branch'}`
              : `Send what ${supplier || 'this branch'} can`}
        </ActionButton>
        <button onClick={onCancel}
          style={{ background: 'none', border: 'none', fontSize: 13, color: '#5C5C5C', cursor: 'pointer', minHeight: controlHeight(coarse, 'min') }}>
          Cancel
        </button>
      </Actions>
    </div>
  );
}
